import React, { useState, useEffect } from 'react';
import { Package, Truck, AlertTriangle, ArrowLeft, Calendar, CreditCard, ChevronDown, ChevronUp, ShoppingBag, Receipt } from 'lucide-react';
import { Link } from 'react-router-dom';

function OrdersDashboard({ accessToken }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // 🌟 State tracking which order card's details summary panel is toggled open
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  const fetchOrders = () => {
    fetch(`${process.env.REACT_APP_API_URL}/api/products/orders/`, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    })
      .then(res => res.json())
      .then(data => {
        setOrders(Array.isArray(data) ? data : (data.results || []));
        setLoading(false);
      })
      .catch(err => {
        console.error("Error loading order history details:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (accessToken) fetchOrders();
  }, [accessToken]);

  const toggleOrderDetails = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  if (loading) {
    return (
      <div className="text-center py-32 text-[#A6A29A] uppercase tracking-widest text-[11px] font-semibold bg-[#F2F1EC] min-h-screen flex items-center justify-center">
        Retrieving Order Ledger...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F1EC] py-6 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Top Header Panel */}
        <div className="p-6 bg-[#FDFDFB] border border-[#E1DFD7] rounded-md shadow-xs flex flex-col sm:flex-row sm:items-baseline justify-between gap-4">
          <div className="flex items-center gap-3">
            <Package className="w-4 h-4 text-[#9A7B56]" />
            <div>
              <h1 className="text-[#2C2A27] text-sm uppercase tracking-widest font-semibold text-[#9A7B56]">
                My Orders ({orders.length})
              </h1>
              <p className="text-xs text-[#8A867E] font-light mt-0.5">Historical verification ledger of acoustic acquisitions</p>
            </div>
          </div>
          <Link to="/" className="text-[11px] text-[#8A867E] font-semibold uppercase tracking-widest h-9 px-3 border border-transparent hover:border-[#E1DFD7] hover:bg-[#FDFDFB] rounded-sm flex items-center gap-1 hover:text-[#9A7B56] transition-all self-end sm:self-auto">
            <ArrowLeft className="w-3.5 h-3.5" /> Continue Shopping
          </Link>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-16 bg-[#FDFDFB] border border-[#E1DFD7] rounded-md max-w-sm mx-auto p-6 space-y-3 shadow-xs">
            <AlertTriangle className="w-5 h-5 text-[#A6A29A] mx-auto font-light" />
            <p className="text-[#A6A29A] text-xs font-light uppercase tracking-wider">You haven't placed any orders yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              // 🌟 Dynamic Calculation of GST & Base Subtotal (falls back to backend API fields if available)
              const totalAmount = parseFloat(order.total_amount || 0);
              
              // If backend provides explicit tax sum, use it; otherwise compute from items
              const totalGst = order.total_gst !== undefined 
                ? parseFloat(order.total_gst)
                : (order.items || []).reduce((sum, item) => {
                    if (item.total_gst !== undefined) return sum + parseFloat(item.total_gst);
                    const rate = parseFloat(item.gst_rate || item.gst_percentage || 0);
                    const itemTotal = parseFloat(item.price || 0) * (item.quantity || 1);
                    return sum + ((itemTotal * rate) / 100);
                  }, 0);

              const subtotal = order.subtotal !== undefined 
                ? parseFloat(order.subtotal) 
                : Math.max(0, totalAmount - totalGst);

              return (
                <div key={order.id} className="bg-[#FDFDFB] border border-[#E1DFD7] rounded-md p-5 transition-all hover:shadow-md">
                  
                  {/* Main Summary Block */}
                  <div className="flex flex-col md:flex-row justify-between gap-6">
                    <div className="space-y-3 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] tracking-widest font-bold text-[#8A867E] bg-[#F2F1EC] px-2 py-0.5 rounded-sm uppercase">Shiprocket ID</span>
                        <span className="text-xs font-mono font-medium text-[#5A5750]">{order.shiprocket_order_id || "Awaiting Generation"}</span>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-[#8A867E] pt-1">
                        <p className="flex items-center gap-2 font-light">
                          <Calendar className="w-3.5 h-3.5 text-[#A6A29A]" />
                          <span>Ordered: <span className="font-normal text-[#2C2A27]">{new Date(order.created_at).toLocaleDateString('en-IN', { dateStyle: 'medium' })}</span></span>
                        </p>
                        <p className="flex items-center gap-2 font-light">
                          <CreditCard className="w-3.5 h-3.5 text-[#A6A29A]" />
                          <span>Payment: <b className="text-[#5A5750] font-medium uppercase tracking-wide text-[11px]">{order.payment_method}</b></span>
                        </p>
                      </div>

                      <div className="pt-1">
                        <p className="text-xs font-medium uppercase tracking-wide text-[#8A867E]">
                          Total Value Paid: <span className="font-semibold text-base text-[#2C2A27] normal-case ml-1">₹{totalAmount.toFixed(2)}</span>
                          {totalGst > 0 && (
                            <span className="ml-2 text-[10px] text-[#8A867E] font-light normal-case">
                              (incl. ₹{totalGst.toFixed(2)} GST)
                            </span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Logistics & Interactive Action Controls */}
                    <div className="flex flex-col justify-between items-start md:items-end gap-4 min-w-[200px] border-t md:border-t-0 pt-4 md:pt-0 border-[#E1DFD7]">
                      <div className="flex items-center gap-2 md:text-right">
                        <div className={`w-1.5 h-1.5 rounded-full ${
                          order.status === 'Cancelled' ? 'bg-amber-700' :
                          order.status === 'Delivered' ? 'bg-emerald-600' : 'bg-amber-500'
                        }`} />
                        <span className={`text-[11px] font-bold uppercase tracking-widest ${
                          order.status === 'Cancelled' ? 'text-amber-800' :
                          order.status === 'Delivered' ? 'text-emerald-700' : 'text-amber-600'
                        }`}>
                          {order.status}
                        </span>
                      </div>

                      <div className="flex gap-2 w-full md:w-auto">
                        <button 
                          onClick={() => toggleOrderDetails(order.id)}
                          className="flex-1 md:flex-initial flex items-center justify-center gap-1.5 border border-[#E1DFD7] hover:border-[#9A7B56] text-[#5A5750] hover:text-[#9A7B56] text-[10px] font-semibold uppercase tracking-widest px-4 h-9 rounded-sm transition-colors bg-white shadow-2xs"
                        >
                          {expandedOrderId === order.id ? (
                            <>Hide Details <ChevronUp className="w-3.5 h-3.5" /></>
                          ) : (
                            <>View Order <ChevronDown className="w-3.5 h-3.5" /></>
                          )}
                        </button>
                        
                        {order.status !== 'Cancelled' && order.shiprocket_shipment_id && order.shiprocket_shipment_id !== "None" && (
                          <a 
                            href={`https://shiprocket.co/tracking/${order.shiprocket_shipment_id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex-1 md:flex-initial flex items-center justify-center gap-1.5 bg-[#F2F1EC] hover:bg-[#9A7B56] border border-[#E1DFD7] hover:border-[#9A7B56] text-[#5A5750] hover:text-white text-[10px] font-semibold uppercase tracking-widest px-4 h-9 rounded-sm transition-all duration-300"
                          >
                            <Truck className="w-3.5 h-3.5" />
                            Track
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Render real database snapshots including images, sizes, and GST */}
                  {expandedOrderId === order.id && (
                    <div className="mt-5 pt-5 border-t border-[#E1DFD7] space-y-4 bg-[#FDFDFB] rounded-sm transition-all">
                      <h3 className="text-[10px] font-bold tracking-widest text-[#8A867E] uppercase flex items-center gap-2 mb-1">
                        <ShoppingBag className="w-3.5 h-3.5 text-[#9A7B56]" /> Itemized Acquisition Summary
                      </h3>
                      
                      <div className="divide-y divide-[#F2F1EC]">
                        {order.items && order.items.length > 0 ? (
                          order.items.map((item, index) => {
                            const gstRate = parseFloat(item.gst_rate || item.gst_percentage || 0);

                            return (
                              <div key={item.id || index} className="py-3 flex items-center justify-between gap-4 text-xs text-[#2C2A27]">
                                <div className="flex items-center gap-3">
                                  {/* Product Thumbnail Layout Image */}
                                  {item.photo_url ? (
                                    <img 
                                      src={item.photo_url} 
                                      alt={item.product_name} 
                                      className="w-12 h-12 object-cover rounded border border-[#E1DFD7] bg-[#F2F1EC]"
                                    />
                                  ) : (
                                    <div className="w-12 h-12 flex items-center justify-center bg-[#F2F1EC] border border-[#E1DFD7] rounded text-[#A6A29A]">
                                      <Package className="w-4 h-4" />
                                    </div>
                                  )}
                                  
                                  {/* Product Details Text Block */}
                                  <div className="space-y-0.5">
                                    <p className="font-medium text-[#5A5750]">{item.product_name}</p>
                                    <div className="flex flex-wrap items-center gap-x-2 text-[11px] text-[#8A867E] font-light">
                                      <span>Qty: <span className="font-normal text-[#5A5750]">{item.quantity}</span></span>
                                      {item.size && (
                                        <>
                                          <span className="text-[#E1DFD7]">•</span>
                                          <span>Size: <span className="font-normal text-[#5A5750] uppercase">{item.size}</span></span>
                                        </>
                                      )}
                                      
                                      {/* 🌟 Item GST Badge */}
                                      {gstRate > 0 ? (
                                        <>
                                          <span className="text-[#E1DFD7]">•</span>
                                          <span className="text-[9px] font-semibold text-[#8A867E] bg-[#F2F1EC] px-1.5 py-0.2 rounded-xs uppercase">
                                            +{gstRate}% GST
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <span className="text-[#E1DFD7]">•</span>
                                          <span className="text-[9px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.2 rounded-xs uppercase">
                                            Exempt
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Individual Item Pricing Snapshot */}
                                <div className="text-right">
                                  <span className="font-mono font-medium text-[#5A5750]">
                                    ₹{(parseFloat(item.price || 0) * (item.quantity || 1)).toFixed(2)}
                                  </span>
                                  {item.quantity > 1 && (
                                    <p className="text-[10px] text-[#8A867E] font-light font-mono mt-0.5">
                                      (₹{parseFloat(item.price || 0).toFixed(2)} each)
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="py-2 text-xs text-[#8A867E] font-light italic">
                            No product items recorded for this order ledger entry.
                          </div>
                        )}
                      </div>

                      {/* 🌟 Order Financial Ledger Breakdown (Subtotal + GST = Total) */}
                      <div className="bg-[#F2F1EC]/60 rounded-sm p-3.5 border border-[#E1DFD7] space-y-2 text-xs">
                        <div className="flex justify-between items-center text-[#8A867E] font-light">
                          <span>Items Subtotal</span>
                          <span className="font-mono text-[#5A5750]">₹{subtotal.toFixed(2)}</span>
                        </div>
                        
                        <div className="flex justify-between items-center text-[#8A867E] font-light">
                          <span className="flex items-center gap-1">
                            <Receipt className="w-3.5 h-3.5 text-[#9A7B56]" />
                            Total GST Tax
                          </span>
                          <span className="font-mono text-[#5A5750]">
                            {totalGst > 0 ? `+₹${totalGst.toFixed(2)}` : '₹0.00'}
                          </span>
                        </div>
                        
                        <div className="border-t border-dashed border-[#E1DFD7] pt-2 flex justify-between items-center text-[#2C2A27] font-semibold">
                          <span>Total Paid</span>
                          <span className="font-mono text-sm">₹{totalAmount.toFixed(2)}</span>
                        </div>
                      </div>

                    </div>
                  )}

                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}

export default OrdersDashboard;